// Core packages
import {
  AfterViewInit, Component, Inject,
  OnDestroy, OnInit
} from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { UsersService } from "src/app/modules/users/users.service";
import { SubUser } from "src/app/shared/models/company/company.model";
import { IUser } from "src/app/shared/models/user/user.interface";
import { User } from "src/app/shared/models/user/user.model";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";

@Component({
  selector: "app-add-sub-user-dialog",
  templateUrl: "./add-sub-user-dialog.component.html",
  styleUrls: ["./add-sub-user-dialog.component.scss"]
})
export class AddSubUserDialogComponent
  implements OnInit, OnDestroy, AfterViewInit {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  user: User = new User();
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  availableUsers = [];
  searchAll = true;

  // BEGIN - Add subUser form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.add"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true
    }
  };
  // END - Add subUser form

  dialogIsLoading = true;

  previousUserId = "";
  currentUserEmail = "";
  userAlreadyExists = false;

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<AddSubUserDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public configService: ConfigService,

    private cookieService: CookieService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private companiesService: CompaniesService,
    private authService: AuthService,
    private helperService: HelperService,
    private usersService: UsersService,
    private apiService: ApiService
  ) {
    dialogRef.disableClose = true;
  }

  /**
   * Init component
   *
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => (this.loggedUser = res))
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Init add form
    this.initForm();

    // Stop loading dialog
    this.dialogIsLoading = false;
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set model
    this.model = this.model as SubUser;

    // Set fields
    this.fields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-4",
            type: "select",
            key: "role",
            templateOptions: {
              label: this.translateService.instant(
                "companies.detail.subUsersTab.add.fields.role"
              ),
              placeholder: this.translateService.instant(
                "companies.detail.subUsersTab.add.fields.role"
              ),
              required: true,
              options: [
                {
                  label: this.translateService.instant("roles.types.master"),
                  value: "master"
                },
                {
                  label: this.translateService.instant(
                    "roles.types.collaborator"
                  ),
                  value: "collaborator"
                }
              ]
            },
          },
          {
            className: "col-8",
            type: "autocomplete",
            key: "email",
            templateOptions: {
              customLabel: this.translateService.instant(
                "companies.detail.subUsersTab.add.fields.name"
              ),
              placeholder: this.translateService.instant(
                "companies.detail.subUsersTab.add.fields.name"
              ),
              required: true,
              bindLabel: "email",
              bindValue: "_id",
              multiple: false,
              minTermLength: 2,
              search$: this.usersService.getList.bind(this),
              getAll: true,
              hideSelected: true,
              addTag: (term: string) => {
                const regex = new RegExp(this.configService.emailPattern);
                if (regex.test(term)) {
                  return term;
                } else {
                  this.toastrService.warning(this.translateService.instant("common.errors.invalid_email_address"));
                  return false;
                }
              },
              addTagText: this.translateService.instant(
                "common.words.addTag"
              ),
              typeToSearchText: this.translateService.instant(
                "common.words.typeToSearch", { minTermLength: 2 }
              ),
              clearAllText: this.translateService.instant(
                "common.words.clearAll"
              ),
              notFoundText: this.translateService.instant(
                "common.words.notFound"
              ),
              loadingText: this.translateService.instant("common.words.loading")
            },
            expressionProperties: {
              'templateOptions.onChange': (value: any) => {
                this.updateValues(value.email);
              }
            },
          },
          {
            className: "col-12 col-md-6",
            type: "input",
            key: "firstName",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.phonebook.firstName"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.phonebook.firstName"
              ),
              required: true,
              minLength: 2,
              maxLength: 50,
              bindLabel: "email",
              bindValue: "_id",
              multiple: false,
              search$: this.usersService.getList.bind(this)
            }
          },
          {
            className: "col-12 col-md-6",
            type: "input",
            key: "lastName",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.phonebook.lastName"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.phonebook.lastName"
              ),
              required: true,
              minLength: 2,
              maxLength: 50
            }
          },
          // {
          //   className: "col-12",
          //   type: "textarea",
          //   key: "notes",
          //   templateOptions: {
          //     label: this.translateService.instant(
          //       "companies.detail.subUsersTab.add.fields.notes"
          //     ),
          //     placeholder: this.translateService.instant(
          //       "companies.detail.subUsersTab.add.fields.notesPlaceholder"
          //     ),
          //     required: false,
          //     minLength: 2,
          //     maxLength: 1000,
          //     rows: 2
          //   },
          //   expressionProperties: {
          //     "templateOptions.description":
          //       '(model[field.key]||"").length+"/" +field.templateOptions.maxLength'
          //   }
          // }
        ]
      }
    ];
  }

  /**
   * After view init logic
   *
   */
  ngAfterViewInit(): void {
    // this.updateValues();
  }

  /**
   * Update firstName/lastName values based on userId changes
   */
  updateValues(value: any): void {
    // if (this.form.controls.userId) {
    //   this.form.controls.userId.valueChanges.subscribe(res => {
    //     if (res) {
    //       const userId = res;
    //       this.usersService.get(userId).subscribe(
    //         res2 => {
    //           if (res2.status) {
    //             this.user.setData(res2.user);

    //             this.form.controls.firstName.setValue(
    //               this.user.get("firstName")
    //             );
    //             this.fields[0].fieldGroup[2].templateOptions.readonly = true;

    //             this.form.controls.lastName.setValue(this.user.get("lastName"));
    //             this.fields[0].fieldGroup[3].templateOptions.readonly = true;
    //           }
    //         },
    //         err => {
    //           this.helperService.handleError(err);
    //         }
    //       );
    //     }
    //   });
    // }
    if (this.previousUserId !== value) {
      this.previousUserId = value;
      const regex = new RegExp(this.configService.emailPattern);
      const roleControl = this.form.get("role");
      const firstNameControl = this.form.get("firstName");
      const lastNameControl = this.form.get("lastName");

      //roleControl.setValue("");
      roleControl.enable();
      //firstNameControl.setValue("");
      firstNameControl.enable();
      //lastNameControl.setValue("");
      lastNameControl.enable();

      if (this.previousUserId && !regex.test(this.previousUserId)) {
        this.usersService.get(this.previousUserId).subscribe(
          res => {
            if (res.status) {
              this.currentUserEmail = res.data.email;
              if (res.data.firstName) {
                firstNameControl.setValue(res.data.firstName);
                firstNameControl.disable();
              }
              if (res.data.lastName) {
                lastNameControl.setValue(res.data.lastName);
                lastNameControl.disable();
              }

              // current login Company: check user exists in current company
              if (res.data.userRoles
                && Array.isArray(res.data.userRoles)
                && res.data.userRoles.length) {
                const index = res.data.userRoles.findIndex((obj: any) => {
                  return obj.company && obj.company._id === this.data.companyId;
                });
                if (index > -1) {
                  // user already exists
                  this.userAlreadyExists = true;
                  roleControl.setValue(res.data.userRoles[index].role.type);
                  roleControl.disable();
                  this.toastrService.warning(this.translateService.instant("users.userExists"));
                } else {
                  this.userAlreadyExists = false;
                }
              }

            }
          },
          err => {
            this.helperService.handleError(err);
          }
        );
      } else {
        this.currentUserEmail = "";
        this.userAlreadyExists = false;
      }
    }
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(data?: any): void {
    this.dialogRef.close(data);
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(): void {
    // set value and get value disabled
    const model = {
      email: this.form.value.email ? this.form.value.email : this.form.controls.email.value,
      firstName: this.form.value.firstName ? this.form.value.firstName : this.form.controls.firstName.value,
      lastName: this.form.value.lastName ? this.form.value.lastName : this.form.controls.lastName.value,
      role: this.form.value.role ? this.form.value.role : this.form.controls.role.value,
      userId: undefined
    };
    // model.email: check value like _id and change it with user email
    const regex = new RegExp(this.configService.emailPattern);
    if (this.previousUserId && !regex.test(this.previousUserId)) {
      model.email = this.currentUserEmail;
      model.userId = this.previousUserId;
    }

    // Prevent invalid form submit
    if (this.form.invalid) {
      console.warn('form', this.form);
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return;
    }

    // Remove blank values from model
    Object.keys(model).forEach((key: string) => {
      if (typeof model[key] === 'string' && model[key].trim() === "") {
        model[key] = undefined;
      }
    });

    // const newModel = model;

    // Set additional user data
    // newModel.firstName = this.user.firstName;
    // newModel.lastName = this.user.lastName;
    // newModel.email = this.user.get("email");

    // Activate submit button loading status
    this.formBtnOptions.active = true;
    model["companyId"] = this.data.companyId

    // Create subUser for company
    this.createSubUser(model);
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  createSubUser(model: any): void {
    this.subscriptions.push(
      this.companiesService
        .createSubUser(this.data.companyId, model)
        .pipe(finalize(() => (this.formBtnOptions.active = false)))
        .subscribe(
          res => {
            if (res.status) {
              this.toastrService.success(res.message);
              this.onClose({ createdSubUser: true });
              return;
            }
            this.toastrService.error(
              this.translateService.instant(
                "common.errors.unknown_server_error"
              )
            );
          },
          err => {
            // Choose one of the following error handling method.
            // 1) First one show a message right under the form fields
            //    (if the form is properly setted)
            // 2) Second one is used to show a mat-error under each field
            //    in the dynamic form (is meant to be used only in dynamicForm)
            // 3) Third one show toastr notifications for each error
            // 4) Fourth one is used to show mat-error under each field
            //    in the ngx-formly form (is mean to be used only with ngx-formly)
            // this.helperService.handleFormError(form, err);
            // this.helperService.handleDynamicFormError(form, err);
            // this.helperService.handleError(err);
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }
}
